/**
 * Centralized access to Vite-injected environment variables.
 *
 * Falls back to QLASH staging defaults so the dev server runs out of the box
 * without requiring a manual .env.local. Override any value by copying
 * .env.example to .env.local and editing.
 */

const orDefault = (value: string | undefined, fallback: string): string =>
  value && value.length > 0 ? value : fallback;

// RTK Query talks to the Laravel monolith via the SAME-ORIGIN proxy path
// `/qlash-api/*` in both DEV and PROD — the monolith's CORS middleware is
// misconfigured (supports_credentials: true + allowed_origins: ['*'] is
// invalid per the CORS spec, so no Access-Control-* headers are emitted)
// and a cross-origin request from the browser is blocked. The proxy is
// resolved server-side: Vite dev server in DEV, CloudFront behavior on
// the PROD-TmaStack distribution in PROD (both forward to VITE_API_URL).
//
// Other backends (qnews, qavatar, qriots, qbot, v2-api) have proper CORS
// and stay direct — only this one needs proxying.
const isDev = import.meta.env.DEV;

export const env = {
  apiUrl: '/qlash-api',
  newsApiUrl: isDev
    ? '/qnews-api'
    : orDefault(
        import.meta.env.VITE_NEWS_API_URL as string | undefined,
        'https://qnews.qlash.gg',
      ),
  avatarApiUrl: isDev
    ? '/qavatar-api'
    : orDefault(
        import.meta.env.VITE_AVATAR_API_URL as string | undefined,
        'https://qavatar.qlash.gg',
      ),
  qriotsApiUrl: isDev
    ? '/qriots-api'
    : orDefault(
        import.meta.env.VITE_QRIOTS_API_URL as string | undefined,
        'https://qriots.qlash.gg',
      ),
  // QBot — Clash Royale data + AI coaching. Same Logto API resource as the
  // backoffice (https://qbot.qlash.gg) so no new Logto registration needed.
  qbotApiUrl: isDev
    ? '/qbot-api'
    : orDefault(
        import.meta.env.VITE_QBOT_API_URL as string | undefined,
        'https://qbot.qlash.gg',
      ),
  // QSocials — friend/follow + presence + co-watching + cheers. Wave 1 ships
  // Telegram-only. Audience: `https://qsocials.qlash.gg` (separate Logto API
  // resource from the Laravel monolith). In dev proxied through Vite to
  // bypass CORS — same pattern as qriots/qbot.
  qsocialsApiUrl: isDev
    ? '/qsocials-api'
    : orDefault(
        import.meta.env.VITE_QSOCIALS_URL as string | undefined,
        'https://qsocials.qlash.gg',
      ),
  // QDuels — 1v1 Clash Royale duel challenges sent from the friends list.
  // Dedicated Logto API resource: `https://qduels.qlash.gg/api` (note the
  // `/api` suffix in the audience; the base URL itself has no suffix). In dev
  // proxied through Vite to bypass CORS — same pattern as qriots/qbot/qsocials.
  qduelsApiUrl: isDev
    ? '/qduels-api'
    : orDefault(
        import.meta.env.VITE_QDUELS_API_URL as string | undefined,
        'https://qduels.qlash.gg',
      ),
  // QChess — chess tournaments + 1v1 duels hosted on Lichess, with a QLASH
  // leaderboard. Dedicated Logto API resource: `https://qchess.qlash.gg/api`
  // (the `/api`-suffixed audience convention from qriots/qduels). In dev
  // proxied through Vite to bypass CORS — same pattern as the other backends.
  // Dev target is the MacMini over Tailscale (`http://100.94.221.52:3600`).
  qchessApiUrl: isDev
    ? '/qchess-api'
    : orDefault(
        import.meta.env.VITE_QCHESS_API_URL as string | undefined,
        'https://qchess.qlash.gg',
      ),
  // QClans — Clash of Clans vertical (stats, clan-war/CWL/Capital tracking,
  // QLASH competitions & leaderboards, AI coaching). Telegram-first. Dedicated
  // Logto API resource: `https://qclans.qlash.gg/api` (the `/api`-suffixed
  // audience convention from qriots/qduels/qchess; the base URL has no suffix).
  // In dev proxied through Vite to bypass CORS — same pattern as the other
  // backends. Dev target is the MacMini over Tailscale (`http://100.94.221.52:3700`).
  qclansApiUrl: isDev
    ? '/qclans-api'
    : orDefault(
        import.meta.env.VITE_QCLANS_API_URL as string | undefined,
        'https://qclans.qlash.gg',
      ),
  // QClans dev-bypass token. qclans-api accepts `X-Dev-Bypass: <token>` (+
  // optional `X-Dev-User`) only when it runs NODE_ENV=development — a local
  // testing escape hatch for before the "Qlash API QClans" Logto resource is
  // registered + granted (a P6/deploy task). When set (dev only), qclansApi
  // attaches it AND skips the Logto Bearer path. Never set in prod builds; the
  // value is public in the bundle, so the API must keep it dev-gated server-side.
  qclansDevBypass: isDev
    ? orDefault(import.meta.env.VITE_QCLANS_DEV_BYPASS as string | undefined, '')
    : '',
  qclansDevUser: isDev
    ? orDefault(import.meta.env.VITE_QCLANS_DEV_USER as string | undefined, '')
    : '',
  // QBank — virtual currency / loyalty (.NET, owned by Gionko). SPIKE: read-only
  // wallet balance + EXP level. Dev proxies through Vite to a LOCAL QBank
  // (`http://100.94.221.52:8080`) running its internal test OIDC; override the
  // dev proxy target with VITE_QBANK_API_URL to test against prod. Prod URL is
  // wallet.qlash.gg (our QBank fork; aud = the "Qlash API Wallet" Logto resource).
  qbankApiUrl: isDev
    ? '/qbank-api'
    : orDefault(
        import.meta.env.VITE_QBANK_API_URL as string | undefined,
        'https://wallet.qlash.gg',
      ),
  // QBank LOCAL spike: the `sub` to mint an internal test-OIDC token for (against a
  // local QBank). Only used when qbankDevTestOidc is on.
  qbankDevSub: isDev
    ? orDefault(import.meta.env.VITE_QBANK_DEV_SUB as string | undefined, 'tma-dev-user')
    : '',
  // When 'true' (dev only) use QBank's INTERNAL test OIDC — local spike against a
  // local QBank. DEFAULT OFF → dev uses the REAL Logto path (e.g. dev-tma against
  // prod wallet.qlash.gg). Set VITE_QBANK_DEV_TEST_OIDC=true only for local spikes.
  qbankDevTestOidc: isDev && (import.meta.env.VITE_QBANK_DEV_TEST_OIDC as string | undefined) === 'true',
  // Customization microservice (banners, IAP, purchases). Mobile-app calls
  // `https://v2-api.qlash.gg/customization` directly — we proxy through Vite
  // in dev to bypass CORS, same pattern as the other backends.
  customizationApiUrl: isDev
    ? '/qlash-customization'
    : orDefault(
        import.meta.env.VITE_CUSTOMIZATION_API_URL as string | undefined,
        'https://v2-api.qlash.gg/customization',
      ),
  // External web shop URL — opened in system browser via openLink when the
  // user taps "Get more banners". Real banner purchases happen in the mobile
  // app (native IAP); the Mini App stays read-only for Phase 1.
  shopUrl: orDefault(
    import.meta.env.VITE_SHOP_URL as string | undefined,
    'https://qlash.gg/shop',
  ),
  wsUrl: orDefault(
    import.meta.env.VITE_WS_URL as string | undefined,
    'wss://ws.tournaments.qlash.gg/connection/websocket',
  ),
  logtoEndpoint: orDefault(
    import.meta.env.VITE_LOGTO_ENDPOINT as string | undefined,
    'https://stg-account.qlash.gg/',
  ),
  logtoAppId: orDefault(
    import.meta.env.VITE_LOGTO_APP_ID as string | undefined,
    'qdaeddjw8d0jdw4r884ml',
  ),
  logtoResource: orDefault(
    import.meta.env.VITE_LOGTO_RESOURCE as string | undefined,
    'https://api.tournaments.qlash.gg',
  ),
  telegramBotUsername: orDefault(
    import.meta.env.VITE_TG_BOT_USERNAME as string | undefined,
    'QLASH_APP_bot',
  ),
  platformId: orDefault(
    import.meta.env.VITE_PLATFORM_ID as string | undefined,
    'qlash',
  ),
  enableEruda: import.meta.env.VITE_ENABLE_ERUDA === 'true',
  // Admin-only build gate. When `true`, AppShell shows <AccessDenied /> to any
  // authenticated user whose `is_admin !== true`. Used by the DEV bot
  // (`@QLASH_DEV_bot` at `dev-tma.qlash.gg`) to restrict access to the
  // team while PROD (`tma.qlash.gg`) keeps it disabled for everyone.
  // Default `false` so misconfiguration favors openness, not lockout.
  adminGateEnabled: import.meta.env.VITE_ADMIN_GATE_ENABLED === 'true',
  // Per-feature visibility flags. Default ON (anything but the literal string
  // `'false'` keeps the feature visible) so DEV builds without overrides keep
  // showing everything. The PROD build (`.env.production`) sets these to
  // `false` to hide features still being incubated from the public TMA at
  // `tma.qlash.gg`. Toggle to `true` when ready to ship the feature publicly.
  featureAvatarsEnabled: import.meta.env.VITE_FEATURE_AVATARS_ENABLED !== 'false',
  featureQriotsEnabled: import.meta.env.VITE_FEATURE_QRIOTS_ENABLED !== 'false',
  featureClashRoyaleEnabled: import.meta.env.VITE_FEATURE_CR_ENABLED !== 'false',
  featureChessEnabled: import.meta.env.VITE_FEATURE_CHESS_ENABLED !== 'false',
  featureCocEnabled: import.meta.env.VITE_FEATURE_COC_ENABLED !== 'false',
  // QBank spike. PROD build (`.env.production`) must set VITE_FEATURE_QBANK_ENABLED=false.
  // Even if left on in prod it degrades gracefully (no dev token → 401 → "—").
  featureQbankEnabled: import.meta.env.VITE_FEATURE_QBANK_ENABLED !== 'false',
  isDev: import.meta.env.DEV,
} as const;
